import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import Colors from '../../../../ui/global/Colors';
import Fonts from '../../../../ui/global/Fonts';
import Icon from '../../../../ui/global/Icon';
import { useUser } from '../../../../backend/useUser';
import LoadingSpinner from '../../../../ui/components/LoadingSpinner';
import Button from '../../../../ui/components/Button';
import Tooltip from '../../../../ui/components/Tooltip';
import MenuPanel from '../../../../ui/components/MenuPanel';
import AudioPlayer from '../../../../ui/components/AudioPlayer';

const Root = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    max-width: 40vw;
    height: 100%;
    line-height: 1.2em;
    gap: 10px;

    @media (max-width: 650px) {
        max-width: 80vw;
    }
`;

const TitleContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 0 7px 0 12px;
    margin: 0 auto -5px auto;
    z-index: 100;
`;

const Title = styled.h4`
    width: 100%;
    text-align: left;
    font-family: ${Fonts.lexendMedium.fontFamily};
    font-weight: ${Fonts.lexendMedium.fontWeight};
    font-size: 14px;
    color: ${Colors.grey1};
    padding: 0 4px;
    margin: 0;
    z-index: 10;
`;

const Grid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    width: 100%;
    height: auto;
    gap: 12px;
    align-items: start;
`;

const Together = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 5px;
`;

const EmptyStateContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    text-align: center;
    gap: 12px;
`;

const EmptyStateText = styled.p`
    max-width: 140px;
    font-family: ${Fonts.lexendSemiLight.fontFamily};
    font-weight: ${Fonts.lexendSemiLight.fontWeight};
    color: ${Colors.grey4};
    font-size: 14px;
    line-height: 1.2em;
    padding: 0;
    margin: 0;
`;

interface SoundMenuProps {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    selectedSoundIndex?: number | null;
    onSoundSelected: (soundId: string, soundUrl: string, soundName: string) => void;
    onSoundUploaded: (asset: string) => void;
    isGeneratingGame: boolean;
    isUpdatingGameSound: boolean;
}

// Example sounds for demonstration
const EXAMPLE_SOUNDS = [
    { id: '1', name: 'Background Music', url: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3' },
    { id: '2', name: 'Jump', url: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-2.mp3' },
    { id: '3', name: 'Coin Collect Long Title', url: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-3.mp3' },
    { id: '4', name: 'Game Over', url: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-4.mp3' },
    { id: '5', name: 'Level Complete', url: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-5.mp3' },
    { id: '6', name: 'Power Up', url: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-6.mp3' },
    { id: '7', name: 'Enemy Hit', url: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-7.mp3' },
    { id: '8', name: 'Menu Click', url: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-8.mp3' },
];

const SoundMenu: React.FC<SoundMenuProps> = ({ 
    isOpen, 
    onOpenChange, 
    selectedSoundIndex, 
    onSoundSelected, 
    onSoundUploaded, 
    isGeneratingGame, 
    isUpdatingGameSound 
}) => {
    const { user, loading: userLoading } = useUser();
    const [mySounds, setMySounds] = useState(EXAMPLE_SOUNDS);
    const [loading, setLoading] = useState(false);
    const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);

    const handleCreateNewSound = () => {
        window.open('/sound', '_blank');
        onOpenChange(false);
    };

    const handleUploadSound = () => {
        // Create a hidden file input element
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = 'audio/*'; // Accept only audio files
        fileInput.style.display = 'none';
        
        // Add event listener for when a file is selected
        fileInput.addEventListener('change', (event) => {
            const target = event.target as HTMLInputElement;
            if (target.files && target.files.length > 0) {
                const selectedFile = target.files[0];
                
                // Convert the file to base64
                const reader = new FileReader();
                reader.onload = (e) => {
                    const base64String = e.target?.result as string;
                    // Pass the base64 data to the callback function
                    onSoundUploaded(base64String);
                };
                reader.onerror = (error) => {
                    console.error('Error reading file:', error);
                };
                reader.readAsDataURL(selectedFile);
            }
        });
        
        // Append to body, click to open file dialog, then remove
        document.body.appendChild(fileInput);
        fileInput.click();
        
        // Remove the element after selection (or cancel)
        setTimeout(() => {
            document.body.removeChild(fileInput);
        }, 1000);
    };

    const handleAudioElementPlay = (audioElement: HTMLAudioElement) => {
        // Pause all other audio elements when a new one starts playing
        document.querySelectorAll('audio').forEach(audio => {
            if (audio !== audioElement && !audio.paused) {
                audio.pause();
            }
        });
    };

    return (
        <MenuPanel
            open={isOpen}
            onOpenChange={onOpenChange}
            position='left'
            colorMode="dark"
            isCollapsible={false}
            title={
                <TitleContainer>
                    <Together>
                        <Title>Sound Library</Title>
                        <Tooltip text="Upload Sound" colorMode="dark" >
                            <Button
                                size="tiny"
                                color="ghost white" 
                                icon="upload" 
                                onClick={handleUploadSound} 
                                disabled={isGeneratingGame || isUpdatingGameSound}
                            />
                        </Tooltip>
                    </Together>
                    <Tooltip text="Close" colorMode="dark" >
                        <Button
                            size="tiny"
                            color="ghost white" 
                            icon="delete" 
                            onClick={() => onOpenChange(false)} 
                        />
                    </Tooltip>
                </TitleContainer>
            }
        >
            <Root>
                {userLoading ? (
                    <LoadingSpinner colorMode='dark' />
                ) : mySounds.length > 0 ? (
                    <Grid>
                        {mySounds.map((sound, index) => (
                            <AudioPlayer key={sound.id}
                                src={sound.url}
                                title={sound.name}
                                colorMode="dark"
                                // variant="mini"
                                onAudioElementPlay={handleAudioElementPlay}
                                onBackgroundClick={() => onSoundSelected(sound.id, sound.url, sound.name)}
                            />
                        ))}
                    </Grid>
                ) : !loading ? (
                    <EmptyStateContainer>
                        <Icon icon="music" color={Colors.grey6} size={48} />
                        <EmptyStateText>No sounds created yet.</EmptyStateText>
                    </EmptyStateContainer>
                ) : (
                    <></>
                )}
                {loading && (
                    <LoadingSpinner colorMode='dark' />
                )}
            </Root>
        </MenuPanel>
    );
};

export default SoundMenu;